/**
 * Service d'authentification pour inscription, mot de passe oublié et définition de mot de passe
 */
import { AUTH_BASE_URL } from '../config/constants';

/**
 * Inscription - crée un compte et envoie un email de confirmation
 * @param {Object} params
 * @param {string} params.email
 * @param {string} [params.username]
 * @returns {Promise<{ success: boolean, message?: string, error?: string, details?: Array }>}
 */
export async function register({ email, username }) {
  const response = await fetch(`${AUTH_BASE_URL}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, username }),
  });
  const data = await response.json();
  if (!response.ok) {
    return {
      success: false,
      error: data.error || 'Erreur lors de l\'inscription',
      details: data.details,
    };
  }
  return { success: true, message: data.message };
}

/**
 * Définit le mot de passe (confirmation email ou mot de passe oublié)
 * @param {Object} params
 * @param {string} params.token
 * @param {string} params.password
 * @returns {Promise<{ success: boolean, message?: string, error?: string }>}
 */
export async function setPassword({ token, password }) {
  const response = await fetch(`${AUTH_BASE_URL}/auth/set-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ token, password }),
  });
  const data = await response.json();
  if (!response.ok) {
    return {
      success: false,
      error: data.error || 'Erreur lors de la définition du mot de passe',
    };
  }
  return { success: true, message: data.message };
}

/**
 * Demande de réinitialisation de mot de passe
 * @param {Object} params
 * @param {string} params.email
 * @returns {Promise<{ success: boolean, message?: string, error?: string }>}
 */
export async function forgotPassword({ email }) {
  const response = await fetch(`${AUTH_BASE_URL}/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email }),
  });
  const data = await response.json();
  if (!response.ok) {
    return {
      success: false,
      error: data.error || 'Erreur lors de la demande',
    };
  }
  return { success: true, message: data.message };
}

/**
 * Vérifie si un token de réinitialisation est valide
 * @param {string} token
 * @returns {Promise<{ success: boolean, valid?: boolean }>}
 */
export async function validateResetToken(token) {
  const response = await fetch(
    `${AUTH_BASE_URL}/auth/validate-reset-token?token=${encodeURIComponent(token)}`,
    { credentials: 'include' }
  );
  const data = await response.json();
  return { success: response.ok, valid: data.valid };
}

/**
 * Supprime le compte utilisateur (RGPD art. 17)
 * @param {Object} params
 * @param {string} params.confirm - Doit être exactement "SUPPRIMER" pour confirmer
 * @returns {Promise<{ success: boolean, message?: string, error?: string }>}
 */
export async function deleteAccount({ confirm }) {
  const response = await fetch(`${AUTH_BASE_URL}/auth/account`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ confirm }),
  });
  const data = await response.json();
  if (!response.ok) {
    return {
      success: false,
      error: data.error || 'Erreur lors de la suppression du compte',
    };
  }
  return { success: true, message: data.message };
}
